import * as React from 'react';
// Not the init/plain barrel: it re-exports the launcher, and vitest.setup renders this provider, so the barrel would load
// the real launcher before its own test could mock what it imports.
import { clearPlainSession } from '../../init/plain/plain';
import { usePendoAnalytics } from './analytics-provider-pendo.hooks';
import { AnalyticsContext } from './analytics-provider.context';

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

/**
 * The AnalyticsProvider is a component that provides user analytics context to the app.
 *
 * @param props - The props for the AnalyticsProvider
 * @returns
 */
function AnalyticsProvider(props: AnalyticsProviderProps) {
  const { children } = props;

  const [{ accountId, accountName, visitorId, visitorEmail }, dispatch] = React.useReducer(
    (
      previousState: {
        accountId: string | null;
        accountName: string | null;
        visitorId: string | null;
        visitorEmail: string | null;
      },
      action: {
        type: 'update' | 'clear';
        payload?: {
          accountId?: string | null;
          accountName?: string | null;
          visitorId?: string | null;
          visitorEmail?: string | null;
        };
      },
    ) => {
      switch (action.type) {
        case 'update':
          return { ...previousState, ...action.payload };
        case 'clear':
          return { accountId: null, accountName: null, visitorId: null, visitorEmail: null };
      }
    },
    {
      accountId: null,
      accountName: null,
      visitorId: null,
      visitorEmail: null,
    },
  );

  const { handleOnIdentify: handleOnIdentifyPendo, handleOnClearSession: handleOnClearSessionPendo } =
    usePendoAnalytics({ visitorId, dispatch });

  const handleOnIdentify = (
    userId: string,
    organizationId?: string | null,
    organizationName?: string | null,
    userEmail?: string | null,
  ) => {
    handleOnIdentifyPendo(userId, organizationId, organizationName, userEmail);
  };

  const handleOnClearSession = () => {
    // Sign-out doesn't reload the page, so drop the support chat session before the next person signs in.
    clearPlainSession();
    handleOnClearSessionPendo();
  };

  return (
    <AnalyticsContext.Provider
      value={{
        accountId,
        accountName,
        visitorId,
        visitorEmail,
        pendo: window?.pendo,
        onIdentify: handleOnIdentify,
        onClearSession: handleOnClearSession,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
}

export { AnalyticsProvider };
